export interface SimpleChange<T> {
  firstChange: boolean;
  previousValue: T;
  currentValue: T;
  isFirstChange: () => boolean;
}

export type CallBackFunction<T> = (value: T, change?: SimpleChange<T>) => void;

/**
 * @experimental seit 0.2.89
 *
 * Testweise Alternative für Angulars ngOnChanges
 *
 * Kann als Decorator über @Input() geschrieben werden
 *
 * Beispielhaft in der AnbieterFacetteComponent genutzt
 *
 * Es können auch Methoden der nutzende KomponentenKlasse übergeben werden, allerdings
 * muss deren Name als string übergeben werden. Das liegt daran, dass der Decorator intern
 * target nutzt um auf das entsprechende member der ParentKlasse zuzugreifen.
 *
 */
export function OnChange<T = any>(callback: CallBackFunction<T> | string) {
  const cachedValueKey = Symbol();
  const isFirstChangeKey = Symbol();

  return (target: any, key: PropertyKey) => {
    const callBackFn: CallBackFunction<T> =
      typeof callback === 'string' ? target[callback] : callback;
    if (!callBackFn) {
      throw new Error(
        `Methode ${callback} kann in Klasse ${target.constructor.name} nicht gefunden werden`
      );
    }

    Object.defineProperty(target, key, {
      set(value) {
        // change status of "isFirstChange"
        this[isFirstChangeKey] = this[isFirstChangeKey] === undefined;

        // No operation if new value is same as old value
        if (!this[isFirstChangeKey] && this[cachedValueKey] === value) {
          return;
        }

        const oldValue = this[cachedValueKey];
        this[cachedValueKey] = value;
        const simpleChange: SimpleChange<T> = {
          firstChange: this[isFirstChangeKey],
          previousValue: oldValue,
          currentValue: this[cachedValueKey],
          isFirstChange: () => this[isFirstChangeKey]
        };
        callBackFn.call(this, this[cachedValueKey], simpleChange);
      },
      get() {
        return this[cachedValueKey];
      }
    });
  };
}
